/**
 * PostHog: session replay + super-props + funnel events para superficies
 * dual / Mux orientadas a conversión a membresía.
 *
 * Llamar al inicio de sesión (SessionIngressTracker) y cuando el template
 * efectivo cambia (Index) para que replays y eventos lleven variant + Mux.
 */

import { MUX_MEMBERSHIP_TEMPLATE_NAMES } from "@/config/muxMembershipContract";
import { getServerTemplateConfig, getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";
import {
  getPostHogSlackSessionPropertiesPostHog,
  getUserRegionForTracking,
  isPostHogConfigured,
  registerDualCtaVariantContextPostHog,
  registerDualCtaVideoLandingContextPostHog,
  startSessionRecordingPostHog,
} from "@/lib/posthogAnalytics";
import { registerVisitorExperienceContextPostHog } from "@/lib/visitorExperiencePostHog";
import posthog from "posthog-js";

const DUAL_PROMOTED_TEMPLATES = new Set([
  "BaseTemplateArDual",
  "BaseTemplateInternationalDual",
]);

const FUNNEL_SENTINEL = "juaperrone.dual_mux_funnel_session.v1";

export function isDualMuxMembershipSurface(
  templateName?: string | null,
  ctaType?: string | null,
): boolean {
  if (ctaType === "dual") return true;
  if (!templateName) return false;
  if (DUAL_PROMOTED_TEMPLATES.has(templateName)) return true;
  return (MUX_MEMBERSHIP_TEMPLATE_NAMES as readonly string[]).includes(templateName);
}

/**
 * Registra super-props dual/Mux desde __TEMPLATE_CONFIG__ (worker SSR).
 * Idempotente — safe llamar en cada mount de template dual.
 */
export function registerDualMuxMembershipFunnelFromWorkerPostHog(
  templateOverride?: string | null,
): boolean {
  if (!isPostHogConfigured()) return false;

  const cfg = getServerTemplateConfig();
  const template = templateOverride ?? cfg?.templateName ?? null;
  const ctaType = cfg?.ctaType;
  const dv = cfg?.dualVariant;
  const dualMuxSurface = isDualMuxMembershipSurface(template, ctaType);

  // Full experience context (template + dual cascade) for all dual/Mux surfaces.
  registerVisitorExperienceContextPostHog({
    effectiveTemplate: template,
    dualVariant: dv ?? null,
    dualSurface: cfg?.dualSurface ?? null,
    abBucket: cfg?.abBucket,
  });

  if (dv?.id) {
    registerDualCtaVariantContextPostHog({
      dual_variant_id: dv.id,
      dual_variant_geo_scope: dv.assignmentScope,
      dual_variant_geo_code: dv.assignmentGeo,
      dual_variant_stripe_price_id: dv.stripePriceId,
      dual_variant_price_usd: dv.priceUsd,
    });
  }

  if (dualMuxSurface || dv?.muxPlaybackId) {
    registerDualCtaVideoLandingContextPostHog({
      dual_cta_free_enabled: dv?.freeEnabled ?? false,
      dual_cta_has_playback_id: Boolean(dv?.muxPlaybackId),
      dual_cta_has_free_url: Boolean(dv?.freeTelegramUrl),
    });
  }

  const clean: Record<string, string | number | boolean> = {
    membership_funnel_goal: "conversion",
    dual_mux_surface: dualMuxSurface,
  };
  if (template) clean.worker_template = template;
  if (cfg?.abBucket) clean.dual_ab_bucket = cfg.abBucket;
  if (dv?.muxPlaybackId) {
    clean.mux_playback_id_present = true;
    clean.mux_playback_id = dv.muxPlaybackId.slice(0, 48);
  }
  if (dv?.priceUsd != null && dv.priceUsd > 0) {
    clean.membership_price_usd = dv.priceUsd;
  }

  try {
    posthog.register(clean);
  } catch {
    return false;
  }

  return dualMuxSurface || Boolean(dv?.id);
}

/**
 * Evento dedicado para funnels PostHog: visitantes en superficie dual/Mux.
 * Una vez por pestaña (sessionStorage).
 */
export function trackDualMuxMembershipFunnelSessionPostHog(
  extra?: Record<string, unknown>,
): void {
  if (!isPostHogConfigured() || typeof window === "undefined") return;

  try {
    if (sessionStorage.getItem(FUNNEL_SENTINEL) === "1") return;
  } catch {
    /* private mode */
  }

  const cfg = getServerTemplateConfig();
  const template = cfg?.templateName ?? null;
  const dualMuxSurface = isDualMuxMembershipSurface(template, cfg?.ctaType);
  const dv = cfg?.dualVariant;

  if (!dualMuxSurface && !dv?.muxPlaybackId && !dv?.id) return;

  const slack = getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true });
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  posthog.capture("dual_mux_membership_funnel_session", {
    funnel_goal: "membership_conversion",
    template_type: template,
    dual_mux_surface: dualMuxSurface,
    page_path: window.location.pathname,
    page_url: window.location.href,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
    ...geoFlat,
    ...slack,
    dual_variant_id: dv?.id,
    dual_variant_geo_scope: dv?.assignmentScope,
    dual_variant_geo_code: dv?.assignmentGeo,
    dual_variant_price_usd: dv?.priceUsd,
    dual_variant_display_name: dv?.displayName,
    mux_playback_id: dv?.muxPlaybackId?.slice(0, 48),
    worker_template_source: cfg?.templateSource,
    experience_us_state: cfg?.usState,
    experience_city: cfg?.city,
    ...(extra ?? {}),
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "dual_mux_session_started",
    funnel_stage: "1_dual_mux_session",
    funnel_goal: "membership_conversion",
    template_type: template,
    dual_mux_surface: dualMuxSurface,
    page_path: window.location.pathname,
    dual_variant_id: dv?.id,
    ...slack,
  });

  try {
    sessionStorage.setItem(FUNNEL_SENTINEL, "1");
  } catch {
    /* ignore */
  }
}

/** Replay obligatorio en juaperrone.com — best-effort tras init PostHog. */
export function ensureDualMuxSessionRecordingPostHog(): void {
  startSessionRecordingPostHog();
}
